import { useEffect, useMemo, useRef, useState } from "react";
import { Mirror } from "@backstory/player";
import type { Heatmap, SnapshotEvent } from "@backstory/protocol";
import { BREAKPOINT_SIZE, elementIndex, heatmapDocumentHeight, normalizePoints, paintHeat, scrollBuckets } from "./render";

export interface ElementBox {
  selector: string;
  x: number;
  y: number;
  w: number;
  h: number;
  clicks: number;
  rage: number;
  dead: number;
}

/** Resolve element selectors inside the mirrored document to boxes (stage coordinates). */
export function resolveElementBoxes(doc: Document | null, hm: Heatmap): ElementBox[] {
  if (!doc) return [];
  const out: ElementBox[] = [];
  const idx = elementIndex(hm);
  for (const [selector, counts] of idx) {
    let el: Element | null = null;
    try {
      el = doc.querySelector(selector);
    } catch {
      el = null;
    }
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) continue;
    out.push({ selector, x: r.left + (doc.defaultView?.scrollX ?? 0), y: r.top + (doc.defaultView?.scrollY ?? 0), w: r.width, h: r.height, ...counts });
  }
  return out;
}

/**
 * Stage: the representative page snapshot rebuilt by the Player's Mirror in a
 * sandboxed iframe, a canvas heat layer on top, hoverable element boxes with
 * counts, and the whole thing scaled to fit the available width.
 */
export function HeatmapStage({
  heatmap,
  snapshot,
  snapshotLoading = false,
  snapshotError = false,
  containerWidth = 900,
}: {
  heatmap: Heatmap;
  snapshot: SnapshotEvent | null;
  snapshotLoading?: boolean;
  snapshotError?: boolean;
  containerWidth?: number;
}) {
  const size = BREAKPOINT_SIZE[heatmap.breakpoint];
  const scale = Math.min(1, containerWidth / size.w);
  const documentHeight = heatmapDocumentHeight(heatmap.points, size.h);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mirror = useRef<Mirror | null>(null);
  const [boxes, setBoxes] = useState<ElementBox[]>([]);
  const [mirrorError, setMirrorError] = useState(false);
  const points = useMemo(() => normalizePoints(heatmap.points, size.w, size.h), [heatmap.points, size.w, size.h]);

  // Build / rebuild the mirror when the snapshot changes.
  useEffect(() => {
    const host = mirrorRef.current;
    if (!host) return;
    host.innerHTML = "";
    setMirrorError(false);
    mirror.current = new Mirror(host, { baseUrl: snapshot?.url });
    mirror.current.setViewport(size.w, documentHeight);
    if (snapshot) {
      try {
        mirror.current.applySnapshot(snapshot);
        // Heat coordinates are document-relative. The replay mirror restores
        // the checkpoint's scroll position, but a full-page heatmap must begin
        // at document top or the DOM and canvas layers drift apart.
        const doc = mirror.current.iframe.contentDocument;
        doc?.defaultView?.scrollTo(0, 0);
        if (doc?.documentElement) doc.documentElement.scrollTop = 0;
        if (doc?.body) doc.body.scrollTop = 0;
      } catch {
        setMirrorError(true);
      }
    }
    const t = setTimeout(() => setBoxes(resolveElementBoxes(mirror.current?.iframe.contentDocument ?? null, heatmap)), 50);
    return () => clearTimeout(t);
  }, [snapshot, heatmap, size.w, documentHeight]);

  // Paint heat whenever points change.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = size.w;
    c.height = documentHeight;
    if (heatmap.kind !== "scroll") paintHeat(c, points);
    else paintHeat(c, []);
  }, [points, heatmap.kind, size.w, documentHeight]);

  const buckets = scrollBuckets(heatmap);
  const noInteractions = heatmap.kind !== "scroll" && points.length === 0;
  const noSnapshot = !snapshotLoading && (!snapshot || snapshotError || mirrorError);
  return (
    <div className="hm-layout">
      <div className="hm-viewport card" data-testid="heatmap-viewport">
        {/* Pinned to the top of the scroller rather than drawn on the stage:
            the stage is as tall as the recorded page, so a message placed on it
            scrolls away (or starts several screens down). */}
        <div className="hm-msgs">
          {snapshotLoading && <div className="hm-msg">Loading representative page…</div>}
          {noSnapshot && (
            <div className="hm-msg" role="status">
              <strong>Page preview unavailable</strong>
              <span>The interaction layer is still shown. A preview appears after a healthy recording captures this route and viewport.</span>
            </div>
          )}
          {noInteractions && (
            <div className="hm-msg" role="status">
              <strong>No {heatmap.kind} activity</strong>
              <span>Try another date range, route, or viewport.</span>
            </div>
          )}
        </div>
        <div className={`hm-stage${noSnapshot ? " no-snapshot" : ""}`} style={{ width: size.w * scale, height: documentHeight * scale }} data-testid="heatmap-stage">
          <div className="hm-mirror" ref={mirrorRef} style={{ transform: `scale(${scale})`, width: size.w, height: documentHeight }} />
          <canvas ref={canvasRef} style={{ transform: `scale(${scale})`, width: size.w, height: documentHeight }} aria-hidden="true" data-testid="heatmap-canvas" />
          {boxes.map((b) => (
            <div
              key={b.selector}
              className="hm-el"
              tabIndex={0}
              role="img"
              aria-label={`${b.selector}: ${b.clicks} clicks, ${b.rage} rage, ${b.dead} dead`}
              data-testid="heatmap-element"
              style={{ left: b.x * scale, top: b.y * scale, width: b.w * scale, height: b.h * scale }}
            >
              <div className="hm-tip viz-tooltip">
                <div className="t-head mono">{b.selector}</div>
                <div className="t-row"><span className="t-name">Clicks</span><strong>{b.clicks}</strong></div>
                <div className="t-row"><span className="t-name">Rage</span><strong>{b.rage}</strong></div>
                <div className="t-row"><span className="t-name">Dead</span><strong>{b.dead}</strong></div>
              </div>
            </div>
          ))}
          {documentHeight > size.h && (
            <div className="hm-depth" aria-hidden="true">
              {Array.from({ length: Math.ceil(documentHeight / size.h) - 1 }, (_, i) => <span key={i} style={{ top: (i + 1) * size.h * scale }}>{i + 1} viewport{ i === 0 ? "" : "s"}</span>)}
            </div>
          )}
        </div>
      </div>
      <aside className="hm-side">
        <div className="card ins-card">
          <div className="hm-summary">
            <h2 className="mono hm-route" title={heatmap.route}>{heatmap.route}</h2>
            <span className="muted small">{heatmap.sessions.toLocaleString()} {heatmap.sessions === 1 ? "session" : "sessions"}</span>
          </div>
          <p className="muted small">
            {heatmap.kind === "scroll" ? "Share of sessions that scrolled at least this deep." : "Denser color means more interactions. One hue, light to dark."}
          </p>
          {heatmap.kind !== "scroll" && (
            <div className="hm-legend viz">
              <span>few</span>
              <span className="ramp" aria-hidden="true" />
              <span>many</span>
            </div>
          )}
        </div>
        {heatmap.kind === "scroll" && (
          <div className="card ins-card">
            <h2>Scroll depth</h2>
            <div className="hm-scroll viz" role="img" aria-label={`Scroll depth: ${buckets.map((b) => `${b.depthPct}% of page reached by ${b.sessionsPct}% of sessions`).join(", ")}`}>
              <div className="bar" />
              <div className="ticks">
                {(buckets.length ? buckets : [25, 50, 75, 100].map((d) => ({ depthPct: d, sessionsPct: 0 }))).map((b) => (
                  <span key={b.depthPct} style={{ top: `${b.depthPct}%` }}>
                    {b.depthPct}% · {b.sessionsPct.toFixed(0)}% of sessions
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        {(heatmap.elements?.length ?? 0) > 0 && (
          <div className="card ins-card">
            <h2>Top elements</h2>
            <ul className="hm-elements">
              {[...(heatmap.elements ?? [])].sort((a, b) => b.clicks - a.clicks).slice(0, 8).map((e) => (
                <li key={e.selector}>
                  <span className="mono small" title={e.selector}>{e.selector}</span>
                  <span className="muted small">
                    {e.clicks.toLocaleString()} {e.clicks === 1 ? "click" : "clicks"} · {e.rage} rage · {e.dead} dead
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}
